import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import { useCart } from '../contexts/CartContext';
import { productService, categoryService } from '../services';
import type { Product } from '../types/Product';
import type { Category as CategoryType } from '../types/Product';

const Category: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [category, setCategory] = useState<CategoryType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      if (!slug) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // First, fetch the category details by slug
        const categoryResponse = await categoryService.getCategoryBySlug(slug);
        
        if (!categoryResponse.success || !categoryResponse.data) {
          setError('Category not found');
          setIsLoading(false);
          return;
        }
        
        setCategory(categoryResponse.data);
        
        // Then fetch products for this category
        const productsResponse = await productService.getProductsByCategory(
          slug,
          { page: currentPage, limit: 100 }
        );
        
        if (productsResponse.success && productsResponse.data) {
          setProducts(productsResponse.data.data);
          setTotalPages(productsResponse.data.totalPages);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Failed to load category products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoryAndProducts();
  }, [slug, currentPage]);

  const handleAddToCart = (product: Product) => {
    addToCart(product);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <div className="inline-flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
          <span className="text-lg text-gray-600">Loading products...</span>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
          {error || 'Category not found'}
        </h1>
        <p className="text-gray-600">
          The category you're looking for doesn't exist or has been removed.
        </p>
      </div>
    );
  }

  return (
    <div className="pt-8">
      <ProductGrid 
        title={category.name} 
        products={products} 
        onAddToCart={handleAddToCart}
      />
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 pb-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Previous
          </button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              // Show first page, last page, current page, and pages around current
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                      page === currentPage
                        ? 'bg-accent-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 2 ||
                page === currentPage + 2
              ) {
                return (
                  <span key={page} className="px-2 text-gray-500">
                    ...
                  </span>
                );
              }
              return null;
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            Next
          </button>
        </div>
      )}
      
      {/* No products message */}
      {products.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            No products found in this category.
          </p>
        </div>
      )}
    </div>
  );
};

export default Category;